import classNames from 'classnames';
import _ from 'lodash';

import React, { useContext } from 'react';
import { useIntl } from 'react-intl';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Typography,
  TextField,
  Button,
  InputAdornment,
  Tooltip,
  Grid,
} from '@material-ui/core';
import ChevronLeftRoundedIcon from '@material-ui/icons/ChevronLeftRounded';
import ErrorIcon from '@material-ui/icons/Error';
import { ShootingContext } from 'contexts';
import { InputChangeEvent } from 'types';
import { ENG, FR, COLOR, SHOOTING_STEPS } from 'config/constants';
import SaveRoundedIcon from '@material-ui/icons/SaveRounded';
import { Shooting } from 'models';

const LANGUAGES = [
  { value: FR, name: 'french' },
  { value: ENG, name: 'english' },
];

const useStyles = makeStyles({
  textField: {
    '& .MuiIconButton-root': {
      padding: 5,
    },

    '& .MuiOutlinedInput-adornedEnd': {
      paddingRight: 0,
    },

    '& .MuiInputAdornment-positionEnd': {
      color: '#CDD1D6',
      paddingRight: 8,

      '&:hover': {
        color: '#034EA2',
      },
    },
  },
  languageButton: {
    marginRight: 15,

    '&:last-child': {
      marginRight: 0,
    },
    '&.MuiButton-containedSecondary': {
      color: COLOR.WHITE,
    },
  },
  note: {
    height: 95,
  },
  tooltip: {
    color: '#fff',
    fontSize: 12,
    backgroundColor: '#000',
    padding: 15,
  },
  tooltipArrow: {
    color: '#000 !important',
  },
  saveButton: {
    color: COLOR.WHITE,
    '@media (max-width: 767px)': {
      fontSize: 10,
    },
  },
  nextButton: {
    color: COLOR.WHITE,
    '@media (max-width: 767px)': {
      fontSize: 10,
    },
  },
  block: {
    display: 'flex',
    flexDirection: 'row',
    marginTop: 15,
  },
  buttonText: {
    '@media (max-width: 767px)': {
      display: 'none',
    },
  },
  buttons: {
    '@media (max-width: 767px)': {
      display: 'flex',
      flexDirection: 'row',
    },
  },
  backButton: {
    backgroundColor: COLOR.WHITE,
    color: COLOR.LIGHT_BLUE,
    borderColor: COLOR.LIGHT_BLUE,
    marginRight: 20,
    '@media (max-width: 767px)': {
      fontSize: 10,
      marginRight: 5,
      padding: 2,
    },
  },
});

interface Props {
  setActiveStep: (_: number) => void;
  onCancel: () => void;
}

function ClientInfoStep({ setActiveStep, onCancel }: Props) {
  const intl = useIntl();
  const styles = useStyles();

  const {
    shooting,
    updateShootingFields,
    saveShooting,
    recommendedServiceOffers,
    validateClientInfoStep,
    showRecommendationModal,
    ignoreRecommendation,
    serviceOffers,
  } = useContext(ShootingContext);
  const isDraftMode = shooting.state === Shooting.STATE_ENUM.draft;

  const { client_name, client_email, client_phone, client_preferred_language } =
    shooting.shooting_params;
  const fmtPropertyHighlights = intl.formatMessage({
    id: 'shooting.create.labels.propertyHighlights',
  });
  const fmtBack = intl.formatMessage({ id: 'shooting.create.buttons.back' });
  const fmtSaveDraft = intl.formatMessage({ id: 'shooting.create.buttons.saveDraft' });
  const fmtSave = intl.formatMessage({ id: 'shooting.create.buttons.save' });

  const onClientNameChange = (e: InputChangeEvent) => {
    updateShootingFields({
      shooting_params: { client_name: e.target.value },
    });
  };

  const onClientEmailChange = (e: InputChangeEvent) => {
    updateShootingFields({
      shooting_params: { client_email: e.target.value },
    });
  };

  const onClientPhoneChange = (e: InputChangeEvent) => {
    updateShootingFields({
      shooting_params: { client_phone: e.target.value },
    });
  };

  const onPreferedLanguage = (language: string) => {
    updateShootingFields({
      shooting_params: { client_preferred_language: language },
    });
  };

  const onNoteChange = (e: InputChangeEvent) => {
    updateShootingFields({ note: e.target.value });
  };

  const onHighlightNoteChange = (e: InputChangeEvent) => {
    updateShootingFields({ property_highlights: e.target.value });
  };

  const onPreviousStep = () => {
    setActiveStep(SHOOTING_STEPS.SERVICE);
  };

  const onUpdate = () => {
    if (validateClientInfoStep()) {
      saveShooting();
    }
  };

  const onNextStep = () => {
    if (validateClientInfoStep()) {
      setActiveStep(SHOOTING_STEPS.REVIEW);
    }
  };

  return (
    <>
      <Box className='ScrollView'>
        <Grid container className={styles.block}>
          <Grid item md={4}>
            <Typography className='InfoItem_Label'>
              {intl.formatMessage({ id: 'shooting.create.labels.clientName' })}
            </Typography>
          </Grid>
          <Grid item md={8} sm={12} xs={12}>
            <TextField
              className={classNames('InfoItem_Value', styles.textField)}
              variant='outlined'
              fullWidth
              value={client_name}
              onChange={onClientNameChange}
            />
          </Grid>
        </Grid>

        <Grid container className={styles.block}>
          <Grid item md={4}>
            <Typography className='InfoItem_Label'>
              {intl.formatMessage({ id: 'shooting.create.labels.clientEmail' })}
            </Typography>
          </Grid>
          <Grid item md={8} sm={12} xs={12}>
            <TextField
              className={classNames('InfoItem_Value', styles.textField)}
              variant='outlined'
              fullWidth
              value={client_email}
              onChange={onClientEmailChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <Tooltip
                      classes={{ tooltip: styles.tooltip, arrow: styles.tooltipArrow }}
                      title={intl.formatMessage({ id: 'shooting.create.tooltip.clientEmail' })}
                      arrow
                      placement='left'
                    >
                      <ErrorIcon />
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
        <Grid container className={styles.block}>
          <Grid item md={4}>
            <Typography className='InfoItem_Label'>
              {intl.formatMessage({ id: 'shooting.create.labels.clientPhone' })}
            </Typography>
          </Grid>
          <Grid item md={8} sm={12} xs={12}>
            <TextField
              className={classNames('InfoItem_Value', styles.textField)}
              variant='outlined'
              value={client_phone}
              fullWidth
              onChange={onClientPhoneChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <Tooltip
                      classes={{ tooltip: styles.tooltip, arrow: styles.tooltipArrow }}
                      title={intl.formatMessage({ id: 'shooting.create.tooltip.clientPhone' })}
                      arrow
                      placement='left'
                    >
                      <ErrorIcon />
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>

        <Grid container spacing={1} className={styles.block}>
          <Grid item md={4}>
            <Typography className='InfoItem_Label'>
              {intl.formatMessage({ id: 'shooting.create.labels.clientLanguageHeader' })}
            </Typography>
          </Grid>
          <Grid item md={8} sm={12} xs={12}>
            {LANGUAGES.map((m) => {
              return (
                <Button
                  key={m.value}
                  className={styles.languageButton}
                  variant='contained'
                  color={client_preferred_language === m.value ? 'secondary' : 'default'}
                  onClick={() => {
                    onPreferedLanguage(m.value);
                  }}
                >
                  {intl.formatMessage({ id: `shooting.create.labels.${m.name}` })}
                </Button>
              );
            })}
          </Grid>
        </Grid>

        <Grid container className={styles.block}>
          <Grid item md={4}>
            <Typography className='InfoItem_Label'>
              {intl.formatMessage({ id: 'shooting.create.labels.specialInstructions' })}
            </Typography>
          </Grid>
          <Grid item md={8} sm={12} xs={12}>
            <TextField
              className={styles.note}
              variant='outlined'
              multiline
              fullWidth
              rows={3}
              value={shooting.note}
              onChange={onNoteChange}
            />
          </Grid>
        </Grid>

        <Grid container className={styles.block}>
          <Grid item md={4}>
            <Typography className='InfoItem_Label'>{fmtPropertyHighlights}</Typography>
          </Grid>
          <Grid item md={8} sm={12} xs={12}>
            <TextField
              className={styles.note}
              variant='outlined'
              multiline
              fullWidth
              rows={3}
              value={shooting.property_highlights}
              onChange={onHighlightNoteChange}
            />
          </Grid>
        </Grid>
      </Box>
      <Box className='ButtonsBox'>
        <div className={styles.buttons}>
          <Button variant='contained' className='BackButton' onClick={onCancel}>
            {intl.formatMessage({ id: 'shooting.create.buttons.cancel' })}
          </Button>
          <Button
            className={styles.saveButton}
            variant='contained'
            color='secondary'
            onClick={onUpdate}
          >
            <SaveRoundedIcon />
            <Typography className={styles.buttonText}>
              {isDraftMode || !shooting.id ? fmtSaveDraft : fmtSave}
            </Typography>
          </Button>
        </div>

        <div className={styles.buttons}>
          <Button variant='outlined' className={styles.backButton} onClick={onPreviousStep}>
            <ChevronLeftRoundedIcon />
            {fmtBack}
          </Button>
          <Button
            variant='contained'
            color='secondary'
            className={styles.nextButton}
            onClick={onNextStep}
          >
            {intl.formatMessage({ id: 'shooting.create.steps.summary' })}
          </Button>
        </div>
      </Box>
    </>
  );
}

export default ClientInfoStep;
